import { AnimatePresence } from 'framer-motion';
import React, { cloneElement } from 'react';
import { classes } from '../../../utils/commonUtils';
import { useSectionContext } from '../../../views/ats-pages/section/section-context';
import { Image } from '../../image/Image';
import { Link } from '../../Link';
import { AtsImage } from '../AtsImage';
import { AtsItem } from '../AtsItem';
import { ProgressBar } from '../Progress/ProgressBar';
import { AtsAssetSkeleton } from './ats-asset-skeleton';
import css from './ats-asset.module.scss';
import { AtsTitleAsset } from './ats-title-asset';
import { AssetItemProps, AssetProps, AtsAssetProps } from './atsAsset.interfaces';
import { getAtsAssetTitle, getSubtitle, isItem, isNonSubtitlePattern } from './partials/atsAssetViewUtils';

export function AtsAsset(props: AtsAssetProps) {
  if (isItem(props)) {
    return <AtsItemAsset {...props} />;
  } else {
    return <AtsPropsAsset {...props} />;
  }
}

/**
 * An asset with an image, link, text, and subtitle.
 *
 * @param title - the title or name of the asset.
 * @param image - the image source and alternative text.
 * @param [subtitle] - the subtitle of the asset.
 * @param [stretch] - Container receive width & height from parent element.
 *
 * @component
 * @example
 * <Asset
 *   key={asset.id}
 *   title={asset.title}
 *   subtitle={asset.subtitle}
 *   image={{ src: asset.image.src, alt: asset.image.alt }}
 *   stretch
 * />
 */
export function AtsPropsAsset({
  id,
  to,
  image,
  title,
  subtitle,
  progress,
  pattern,
  stretch,
  badge,
  providers,
  matchTitle,
  ...rest
}: AssetProps) {
  return (
    <Link
      title={title}
      className={classes(css.asset, stretch && css.stretch)}
      data-pattern={pattern}
      onMouseDown={(e) => e.preventDefault()}
      to={to}
      {...rest}
    >
      <div className={classes(css.imageContainer, stretch && css.stretch)}>
        {badge !== undefined ? cloneElement(badge, { className: css.badge }) : null}

        <AnimatePresence>
          {progress ? <ProgressBar className={css.progressBar} value={progress} /> : null}
        </AnimatePresence>

        <Image className={css.image} src={image.src} alt={image.alt} />
      </div>

      {title.length > 0 && (
        <h3 className={css.title}>
          <MatchTitleString title={title} matchString={matchTitle || ''} />
        </h3>
      )}

      {subtitle && <p className={css.subtitle}>{subtitle}</p>}
    </Link>
  );
}

function AtsItemAsset({ item, pattern, overwrites, matchTitle, params }: AssetItemProps) {
  const section = useSectionContext('AtsItemAsset');

  const subtitle = isNonSubtitlePattern(pattern) ? undefined : getSubtitle(item, pattern, section.style.type);
  const title = getAtsAssetTitle(item);

  if (item.type === 'title') {
    return (
      <AtsTitleAsset item={item} pattern={pattern} overwrites={overwrites} matchTitle={matchTitle} params={params} />
    );
  }

  const isSeeAll = item.type === 'page' && item.params !== null && item.params.behavior === 'see-all';

  return (
    <AtsItem
      title={title}
      className={css.asset}
      data-pattern={pattern}
      pattern={pattern}
      onMouseDown={(e) => e.preventDefault()}
      item={item}
      overwrites={overwrites}
      params={params}
    >
      <div className={css.imageContainer}>
        <AtsImage className={css.image} item={item} pattern={pattern} />

        {isSeeAll && <h3 className={classes(css.title, css.titleOverImage)}>{title}</h3>}
      </div>

      {!isSeeAll && title !== undefined && title.length > 0 && (
        <h3 className={css.title}>
          <MatchTitleString title={title} matchString="" />
        </h3>
      )}
      {subtitle && <p className={css.subtitle}>{subtitle}</p>}
    </AtsItem>
  );
}

interface MatchTitleStringProps {
  title: string;
  matchString: string;
}

function MatchTitleString({ title, matchString }: MatchTitleStringProps) {
  if (!title.toLowerCase().includes(matchString.toLowerCase())) {
    return <>{title}</>;
  }

  const indexOfMatch = title.toLocaleLowerCase().indexOf(matchString.toLowerCase());

  const start = title.slice(0, indexOfMatch);
  const highlight = title.slice(indexOfMatch, indexOfMatch + matchString.length);
  const end = title.slice(indexOfMatch + matchString.length);

  return (
    <>
      {start}
      <u>{highlight}</u>
      <>{end}</>
    </>
  );
}

AtsAsset.Skeleton = AtsAssetSkeleton;
